import React, { useRef, useEffect } from "react";
import classes from "./OTPInput.scss";

const SANITIZE_NUMERIC = /[^0-9]/g;
const SANITIZE_ALPHANUMERIC = /[^a-zA-Z0-9]/g;

const OTPInput = ({
  value = "",
  onChange,
  onSubmit,
  disabled = false,
  autoFocus = true,
  allowAlphanumeric = false,
  testId,
}) => {
  const inputRefs = useRef([]);
  const OTP_LENGTH = 6;
  const sanitizePattern = allowAlphanumeric
    ? SANITIZE_ALPHANUMERIC
    : SANITIZE_NUMERIC;
  const safeValue = value.slice(0, OTP_LENGTH);
  const digits = safeValue
    .split("")
    .concat(Array(OTP_LENGTH - safeValue.length).fill(""));

  useEffect(() => {
    if (autoFocus && !disabled && inputRefs.current[0]) {
      const timer = setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, disabled]);

  const focusInput = index => {
    if (inputRefs.current[index]) {
      inputRefs.current[index].focus();
    }
  };

  const handleChange = (e, index) => {
    const inputValue = e.target.value.replace(sanitizePattern, "");
    if (!inputValue) return;

    const newDigits = [...digits];
    newDigits[index] = inputValue.slice(-1);
    const newValue = newDigits.join("").slice(0, OTP_LENGTH);
    onChange(newValue);

    if (inputValue && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newDigits = [...digits];
      if (newDigits[index]) {
        newDigits[index] = "";
        onChange(newDigits.join(""));
      } else if (index > 0) {
        newDigits[index - 1] = "";
        onChange(newDigits.join(""));
        focusInput(index - 1);
      }
    } else if (e.key === "Enter" && onSubmit && value.length === OTP_LENGTH) {
      onSubmit();
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(sanitizePattern, "")
      .slice(0, OTP_LENGTH);
    if (pasted) {
      onChange(pasted);
      focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
    }
  };

  return (
    <div className={classes.container} role="group">
      {digits.map((digit, index) => {
        return (
          <input
            // eslint-disable-next-line react/no-array-index-key -- fixed 6-slot array that never reorders
            key={`digit-${index}`}
            ref={el => (inputRefs.current[index] = el)}
            type="text"
            inputMode={allowAlphanumeric ? "text" : "numeric"}
            pattern={allowAlphanumeric ? "[a-zA-Z0-9]*" : "[0-9]*"}
            maxLength={1}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            value={digit}
            data-test-id={`${testId}-digit-${index}`}
            aria-label={`Character ${index + 1} of ${OTP_LENGTH}`}
            className={classes.box}
            onChange={e => handleChange(e, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={handlePaste}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
          />
        );
      })}
    </div>
  );
};
export default OTPInput;
